import { TranslateCompiler, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

/**
 * A deferred compiler that lazy-loads MessageFormat compiler on demand.
 * Before MessageFormat is loaded, it caches translations.
 * Once loaded, it recompiles cached translations with the real compiler.
 */
export class DeferredMessageFormatCompiler implements TranslateCompiler {
    private realCompiler: TranslateCompiler | null = null;
    private cachedTranslations: Map<string, any> = new Map();
    private translateService: TranslateService | null = null;

    // Emit when compiler is ready so components can refresh
    public compilerReady$ = new Subject<void>();

    /**
     * Compile a single translation string.
     */
    compile(value: string, lang: string): string | Function {
        if (!this.realCompiler) {
            return value;
        }

        return this.realCompiler.compile(value, lang);
    }

    /**
     * Compile all translations in an object.
     * Cache them until the real compiler is loaded.
     */
    compileTranslations(translations: any, lang: string): any {
        this.cachedTranslations.set(lang, translations);

        if (!this.realCompiler) {
            return translations;
        }

        return this.realCompiler.compileTranslations(translations, lang);
    }

    /**
     * Set the real compiler and recompile cached translations
     */
    setRealCompiler(compiler: TranslateCompiler, translateService: TranslateService): void {
        this.realCompiler = compiler;
        this.translateService = translateService;

        // Recompile all cached translations with the real compiler
        this.cachedTranslations.forEach((translations, lang) => {
            const compiled = this.realCompiler!.compileTranslations(translations, lang);

            // Update the TranslateService with recompiled translations
            if (this.translateService?.translations) {
                this.translateService.translations[lang] = compiled;
            }
        });

        // Emit event so components know to refresh
        this.compilerReady$.next();
    }

    /**
     * Check if compiler is ready
     */
    isReady(): boolean {
        return this.realCompiler !== null;
    }
}
